import React, { useEffect, useState } from "react";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";
import Header from "../../components/Header";
import { useDispatch, useSelector } from "react-redux";
import {
  getActivityLogs,
  getActivityTypes,
} from "../../redux/Inventory/inventoryActions";
import EmptyState from "../../components/EmptyState";
import moment from "moment";
import { ContentCopy } from "@mui/icons-material";
import toast from "react-hot-toast";

const getActivityTypeBadgeStyle = (activityType) => {
  const styles = {
    STOCK_IN: "border-[#2B8C34] text-[#2B8C34] bg-[#EDF7EE]",
    STOCK_OUT: "border-[#B92020] text-[#B92020] bg-[#FEE2E2]",
    STOCK_TRANSFER: "border-[#0066CC] text-[#0066CC] bg-[#E6F2FF]",
    STOCK_ADJUSTMENT: "border-[#6B46C1] text-[#6B46C1] bg-[#F3E8FF]",
    OUT_OF_STOCK: "border-[#B92020] text-[#B92020] bg-[#FEE2E2]",
    LOW_STOCK: "border-[#D4A407] text-[#D4A407] bg-[#FEF3C7]",
    BACK_IN_STOCK: "border-[#2B8C34] text-[#2B8C34] bg-[#EDF7EE]",
    NEW_PRODUCT: "border-[#6B46C1] text-[#6B46C1] bg-[#F3E8FF]",
    NEW_VARIANT: "border-[#6B46C1] text-[#6B46C1] bg-[#F3E8FF]",
    STOCK_REQUEST: "border-[#D4A407] text-[#D4A407] bg-[#FEF3C7]",
    STOCK_CONFIRMATION: "border-[#2B8C34] text-[#2B8C34] bg-[#EDF7EE]",
  };
  return styles[activityType] || "border-[#5F6D60] text-[#5F6D60] bg-[#F5F5F5]";
};

const handleCopyNumber = (number) => {
  navigator.clipboard.writeText(number);
  toast.success(`${number} copied successfully`);
};

const ActivityLogPage = () => {
  const dispatch = useDispatch();
  const {
    activityLogs,
    activityLogsLoading,
    activityLogsTotalCount,
    activityTypes,
  } = useSelector((state) => state.inventory);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedActivityType, setSelectedActivityType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pageSize, setPageSize] = useState(20);

  // Store all locations for filter (we'll need to extract from data or fetch separately)
  const [allLocations, setAllLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");

  // Extract unique locations from activity logs
  useEffect(() => {
    if (
      activityLogs?.length > 0 &&
      !selectedLocation &&
      !search &&
      !selectedActivityType
    ) {
      const locationSet = new Set();
      activityLogs.forEach((activity) => {
        if (activity.details?.location) {
          locationSet.add(
            JSON.stringify({
              id: activity.details.location_id || activity.location,
              name: activity.details.location,
            })
          );
        }
      });
      const locations = Array.from(locationSet)
        .map((loc) => JSON.parse(loc))
        .sort((a, b) => a.name.localeCompare(b.name));
      if (locations.length > allLocations.length) {
        setAllLocations(locations);
      }
    }
  }, [
    activityLogs,
    selectedLocation,
    search,
    selectedActivityType,
    allLocations,
  ]);

  useEffect(() => {
    // Fetch activity types for filter dropdown
    dispatch(getActivityTypes());
  }, [dispatch]);

  useEffect(() => {
    const filters = {
      page,
      page_size: pageSize,
      search: search || undefined,
      activity_type: selectedActivityType || undefined,
      location_id: selectedLocation || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    };
    dispatch(getActivityLogs(filters));
  }, [
    dispatch,
    page,
    pageSize,
    search,
    selectedActivityType,
    selectedLocation,
    dateFrom,
    dateTo,
  ]);

  const handlePageChange = (paginationData) => {
    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        setPage(paginationData.page);
      }
      if (
        paginationData.pageSize !== undefined &&
        paginationData.pageSize !== pageSize
      ) {
        setPageSize(paginationData.pageSize);
        setPage(1);
      }
    } else {
      setPage(paginationData);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setSelectedActivityType("");
    setSelectedLocation("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasActiveFilters =
    search || selectedActivityType || selectedLocation || dateFrom || dateTo;

  return (
    <>
      <Header text="Activity Log" />

      <div className="mt-5 bg-white rounded-[8px] border border-[#E1E6E1] py-2 px-4">
        {/* Filters */}
        <div className="py-4 flex flex-wrap gap-4 items-center border-b border-[#E1E6E1]">
          <div className="flex-1 min-w-[200px] max-w-[400px]">
            <input
              type="text"
              placeholder="Search in notes and reference..."
              value={search}
              onChange={handleSearch}
              className="appearance-none w-full placeholder:text-[#96A397] 
                placeholder:text-sm text-[#344335] text-base focus:border-[#2B8C34] 
                focus:outline-none focus:shadow-input rounded-lg border border-[#96A397] bg-white px-4 py-2"
            />
          </div>

          <div className="min-w-[150px]">
            <select
              value={selectedActivityType}
              onChange={(e) => {
                setSelectedActivityType(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-[#E1E6E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B8C34] text-sm"
            >
              <option value="">All Activities</option>
              {activityTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[150px]">
            <select
              value={selectedLocation}
              onChange={(e) => {
                setSelectedLocation(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-[#E1E6E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B8C34] text-sm"
            >
              <option value="">All Locations</option>
              {allLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              placeholder="From"
              className="px-4 py-2 border border-[#E1E6E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B8C34] text-sm"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              placeholder="To"
              className="px-4 py-2 border border-[#E1E6E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B8C34] text-sm"
            />
          </div>

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-sm text-[#B92020] hover:bg-[#FEE2E2] rounded-md transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto relative">
          {activityLogsLoading ? (
            <div className="animate-pulse w-full mb-4">
              <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-12 bg-gray-200 rounded-lg w-full"></div>
            </div>
          ) : !activityLogs || activityLogs.length === 0 ? (
            <div className="">
              <EmptyState text="No activities found!" />
            </div>
          ) : (
            <>
              {activityLogs.map((activity, index) => (
                <div
                  key={activity.id}
                  className={` ${
                    index < activityLogs.length - 1
                      ? "border-b border-[#E1E6E1] "
                      : ""
                  }`}
                >
                  <div className="flex justify-between items-center  py-2 px-6 bg-[#F6F7F6] ">
                    <span
                      className={`px-2 py-1 rounded border ${getActivityTypeBadgeStyle(
                        activity.activity_type
                      )} bg-white font-semibold text-xs`}
                    >
                      {activity?.activity_type_display ||
                        activity.activity_type}
                    </span>
                    <span className="text-xs text-[#5F6D60] font-semibold">
                      {moment(activity.timestamp).format(
                        "MMM DD, YYYY hh:mm A"
                      )}
                    </span>
                  </div>
                  {/* Product Name */}
                  <div className=" px-6 py-2 border-t border-[#E1E6E1] flex items-center">
                    <p className="text-sm text-[#5F6D60] font-semibold">
                      Product Name:
                    </p>
                    <div>
                      <span className="text-sm text-[#5F6D60] border rounded-sm py-0.5 px-1 font-semibold ml-2">
                        {activity?.details?.product_name || "N/A"}
                      </span>{" "}
                      /
                      <span className="text-sm text-[#5F6D60] border rounded-sm py-0.5 px-1 font-semibold ml-2">
                        {activity?.details.variant_data?.type || "N/A"}
                      </span>{" "}
                      /
                      <span className="text-sm text-[#5F6D60] border rounded-sm py-0.5 px-1 font-semibold ml-2">
                        {activity?.details?.variant_data?.value || "N/A"}

                        {activity?.details.variant_data?.unit || "N/A"}
                      </span>
                    </div>
                  </div>
                  {/* Unit Added */}
                  {activity?.details.quantity > 0 && (
                    <div className="px-6 py-2 border-t border-[#E1E6E1] flex justify-between items-center">
                      <div className="flex items-center ">
                        <p className="text-sm text-[#5F6D60] font-semibold">
                          Units Added:
                        </p>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded border ml-2 ${
                            activity.activity_type === "STOCK_IN" ||
                            activity.activity_type === "STOCK_TRANSFER-INCOMING"
                              ? "text-[#2B8C34] border-[#2B8C34] bg-[#EDF7EE]"
                              : "text-[#5F6D60] border-[#E1E6E1] bg-[#FFFF]"
                          }`}
                        >
                          {activity.activity_type === "STOCK_IN" ||
                          activity.activity_type === "STOCK_TRANSFER-INCOMING"
                            ? "+"
                            : "-"}
                          {activity.details.quantity}
                        </span>
                      </div>
                      {/* Stock Id */}
                      <div className="border border-[#E1E6E1] px-1 py-1 rounded bg-white w-fit gap-0.5 flex items-center text-xs text-[#5F6D60] font-semibold">
                        {activity?.details?.reference || "---"}
                        <ContentCopy
                          className="text-[#5F6D60] cursor-pointer"
                          style={{ fontSize: "10px" }}
                          onClick={() =>
                            handleCopyNumber(activity?.details?.reference)
                          }
                        />
                      </div>
                    </div>
                  )}
                  {/* Store */}
                  <div className="px-6 py-2 border-t border-[#E1E6E1] flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-[#5F6D60] font-semibold">
                        Store:
                      </p>
                      <span className="text-sm text-[#5F6D60] font-semibold  border border-[#E1E6E1] rounded-md py-0.5 px-1 bg-[#F6F7F6]">
                        {activity?.details?.location || "N/A"}
                      </span>
                    </div>
                    <button className="text-sm text-[#2B8C34] font-semibold">
                      See Stock Details
                    </button>
                  </div>

                  {/* Stock added by */}
                  {activity?.actor && (
                    <div className="flex items-center border-t border-[#E1E6E1] px-6 py-2">
                      <span className="text-sm font-semibold text-[#5F6D60] mr-2">
                        {activity?.activity_type === "STOCK_IN"
                          ? "Stock Added By:"
                          : activity?.activity_type.includes("TRANSFER")
                          ? "Stock Transferred By:"
                          : "Sold By:"}
                      </span>
                      <span className="text-xs font-semibold text-[#2E332F]">
                        {activity?.actor.name || activity?.actor.username}
                      </span>
                      {activity.actor.role && (
                        <span className="text-xs text-[#5F6D60] ml-2 bg-[#F6F7F6] font-semibold border border-[#E1E6E1] px-2 py-1 rounded">
                          {activity.actor.role}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* Pagination */}
          {activityLogs && activityLogs.length > 0 && (
            <div className="border-t border-[#E1E6E1] mt-4">
              <PaginationComponentOne
                totalItems={activityLogsTotalCount || 0}
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ActivityLogPage;
